import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Flex, Pill, TagsInput, Text } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import axios from 'axios';
import toast from 'react-hot-toast';

import { toErrorMessage } from '@util/toErrorMessage';

export default function Taggit({
  reqInfo,
  fetchData = () => {},
  readOnly = false,
  defaultEditing = false,
  onDone = null,
}) {
  const [data, setData] = useState([]);
  const [value, setValue] = useState([]);
  const [tagData, setTagData] = useState([]);
  const [editing, setEditing] = useState(defaultEditing);
  const cancelRef = useRef(null);

  const hasReqInfo =
    reqInfo &&
    typeof reqInfo === 'object' &&
    Object.keys(reqInfo).length > 0 &&
    Object.values(reqInfo).some(
      (val) => val !== null && val !== undefined && val !== ''
    );

  useEffect(() => {
    if (!hasReqInfo) return;
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqInfo?.exercise_id, reqInfo?.variety]);

  function handleValueChange(newValue) {
    const addedTags = newValue.filter((tag) => !value.includes(tag));
    addedTags.forEach((tag) => {
      const existingTag = data.find((item) => item.label === tag);
      if (existingTag) {
        onTagAdd(existingTag.value);
      } else if (tag.length < 3) {
        toast.error('Tag must be at least 3 characters long');
      } else {
        onTagCreate(tag);
      }
    });

    const removedTags = value.filter((tag) => !newValue.includes(tag));
    removedTags.forEach((tag) => {
      const tagDataItem = tagData.find((item) => item.tag.name === tag);
      if (tagDataItem) {
        onTagRemove(tagDataItem.id);
      }
    });
  }

  function searchTags(searchValue) {
    if (cancelRef.current) {
      cancelRef.current.abort();
    }

    if (!searchValue) {
      setData([]);
      return;
    }

    const controller = new AbortController();
    cancelRef.current = controller;

    axios
      .post(
        `/search-all-tags/`,
        {
          ...reqInfo,
          value: searchValue,
        },
        { signal: controller.signal }
      )
      .then(({ data: envelope }) => {
        const uniqueTags = (envelope.response || []).reduce((acc, d) => {
          if (!acc.some((tag) => tag.label === d.name)) {
            acc.push({ label: d.name, value: d.id });
          }
          return acc;
        }, []);
        setData(uniqueTags);
      })
      .catch((error) => {
        if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return;
        setData([]);
      });
  }

  function onTagAdd(tagId) {
    axios
      .post(`/tags/`, {
        ...reqInfo,
        tag_id: tagId,
      })
      .then(() => {
        toast.success('Added!');
        fetchTags();
        fetchData();
      })
      .catch((error) => toast.error(toErrorMessage(error)));
  }

  function onTagCreate(tagName) {
    axios
      .post(`/tags/`, {
        ...reqInfo,
        name: tagName,
      })
      .then(() => {
        toast.success('Added!');
        fetchTags();
        fetchData();
      })
      .catch((error) => toast.error(toErrorMessage(error)));
  }

  function onTagRemove(taggingId) {
    axios
      .post(`/delete-tagging/`, { tagging_id: taggingId })
      .then(() => {
        fetchTags();
        fetchData();
      })
      .catch((error) => toast.error(toErrorMessage(error)));
  }

  function fetchTags() {
    axios
      .post(`/retrieve-entity-tags/`, reqInfo)
      .then(({ data: envelope }) => {
        const rows = envelope.response || [];
        setValue(rows.map((row) => row.tag.name));
        setTagData(rows);
      })
      .catch(() => {
        setValue([]);
        setTagData([]);
      });
  }

  if (!hasReqInfo) return null;

  if (readOnly) {
    return (
      <Flex gap="xs" wrap="wrap" align="center">
        {tagData.length === 0 && (
          <Text size="sm" c="dimmed">
            no tags
          </Text>
        )}
        {tagData.map((row) => (
          <TagPill tag={row.tag} key={row.id} />
        ))}
      </Flex>
    );
  }

  if (editing) {
    return (
      <Flex align="center" gap="xs">
        <TagsInput
          placeholder="Search tags"
          style={{ flexGrow: 1 }}
          value={value}
          onChange={handleValueChange}
          data={data.map((item) => item.label)}
          onSearchChange={searchTags}
          size="sm"
          radius="lg"
        />
        <ActionIcon
          size="xs"
          color="gray"
          radius="xl"
          onClick={() => {
            setEditing(false);
            if (onDone) onDone();
          }}
          variant="transparent"
          aria-label="Done editing tags"
        >
          <IconEdit size={14} />
        </ActionIcon>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="xs">
      <Flex gap="xs" wrap="wrap" align="center">
        {tagData.length === 0 && (
          <Text size="sm" c="dimmed">
            start tagging
          </Text>
        )}
        {tagData.map((row) => (
          <TagPill tag={row.tag} key={row.id} />
        ))}
      </Flex>
      <ActionIcon
        size="xs"
        color="gray"
        radius="xl"
        onClick={() => setEditing(true)}
        variant="transparent"
        aria-label="Edit tags"
      >
        <IconEdit size={14} />
      </ActionIcon>
    </Flex>
  );
}

export function TagPill({ tag }) {
  return (
    <Pill
      styles={{
        root: {
          backgroundColor: 'var(--mantine-color-gray-2)',
        },
      }}
      size="sm"
      fw={500}
    >
      {tag.name}
    </Pill>
  );
}

export function TagPillList({ tags }) {
  if (!tags || tags.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        no tags
      </Text>
    );
  }

  return (
    <Flex gap="xs" wrap="wrap" align="center">
      {tags.map((tag) => (
        <TagPill tag={tag} key={tag.id} />
      ))}
    </Flex>
  );
}
